/**
 * Mini-cart loyalty (Rivo): auth, points/claim rules, affordability, redeem CTAs.
 *
 * Card states (see {@link LoyaltyCart#applyCardState}):
 * - `claimed` — an unused points purchase exists for the product: "Claimed" badge, `0 pts`, Add CTA.
 * - `redeem`  — balance covers the reward cost: outlined Claim CTA.
 * - `locked`  — balance does not cover the cost: card dimmed, grey CTA with a lock, no interaction.
 * Redeemed (used) purchases are excluded by the `{ used: false }` query, so a redeemed reward falls back
 * to `redeem`/`locked` on the next refresh instead of staying badged as claimed.
 * The rail shows for any signed-in customer once Rivo returns a balance, so 0–99 point customers see the
 * locked catalogue. Guests keep `root.hidden` and see nothing.
 *
 * Two rail modes (see {@link LoyaltyCart#refreshLoyalty}):
 * - Normal (no loyalty gift in cart): claimed cards first (by points), then unclaimed by ascending points.
 * - Gift in cart (loyalty-discounted lines in the live cart, falling back to the `[data-loyalty-in-cart-ids]`
 *   child): hide those product cards; force remaining cards to locked (ignore claimed state) ordered by
 *   ascending points. The rewards grid keeps them visible instead, priced at their points cost again
 *   but still badged as Claimed (see {@link LoyaltyCart#readLoyaltyCartState}).
 *
 * Claiming needs a cart the loyalty discount can land on: below the minimum the rewards grid carries in
 * `data-loyalty-min-cart-total`, the CTA opens that section's empty-cart pop-up and no claim is made
 * (see {@link LoyaltyCart#blockedByEmptyCart}) — points are only ever spent on a cart that can take the code.
 *
 * Coupon rewards (rewards grid only, {@link LoyaltyCart#applyCouponCardState}) resolve to a discount code
 * rather than a cart line. The code is never rendered: claiming applies it to the cart, and a claim already
 * on the cart returns the card to `redeem`/`locked` — badged as Claimed — so another coupon can be claimed.
 *
 * Event delegation survives cart section morphs. Option drawer (section render) is mobile-only for multi-variant rewards.
 * Loyalty stays hidden until Rivo is callable: `document` dispatching `rivo-js-loaded`, or `window.RivoJS`
 * already being present — this module is `type="module" fetchpriority="low"`, so the event can land before
 * the listener exists. A `RIVO_LOAD_TIMEOUT_MS` backstop resolves roots whose load never completes, so a
 * missed event can no longer strand a root on its loading skeleton. `reinitializeLoyaltyCart` runs on `section:morph` (after `morphSection`) so loyalty refreshes when the cart section is morphed, including when the loyalty block is injected again. Not on `cart:update`, which often fires before morph.
 * The loyalty root in Liquid uses `data-skip-node-update` so morph does not re-apply server `hidden` over client-controlled visibility after cart line changes.
 *
 * RivoJS response shapes: https://developer-docs.rivo.io/docs/functions
 * - getCustomerDetails → { success, customer: { points_tally, … } }
 * - getCustomerPointsPurchases(opts, page) → { success, points_purchases: [{ points_amount, used_at, reward: { … } }] }
 * - redeemReward(rewardId) → Promise resolving to an object with nested `reward` (or `points_purchase.reward`).
 *   We read reward.product_id, variant_ids (array) / shopify_variant_id / variant_id via {@link pickVariantIdFromReward}; discount for redeem flows comes from the matching `points_purchase` row (see {@link pickCodeFromRedeemResByProdId} and {@link pickDiscountCodeFromPurchase}).
 *   Confirm field names against a live Rivo payload if redemption fails to parse.
 */

import { CartAddEvent, DiscountUpdateEvent, ThemeEvents } from "@theme/events";
import { morphSection } from "@theme/section-renderer";
import { fetchConfig } from "@theme/utilities";
import { getLoyaltyDiscountCfg, isLoyaltyCode } from "./loyalty-discount-utils.js";

/**
 * The live cart as loyalty reads it. Every field is `null` when the cart could not be read.
 * @typedef {{ prodIds: Set<string> | null, codes: Set<string> | null, itemCount: number | null, totalPrice: number | null }} LoyaltyCartState
 */
/** @type {LoyaltyCartState} */
const UNREAD_CART_STATE = { prodIds: null, codes: null, itemCount: null, totalPrice: null };

const RIVO_LOAD_EV = "rivo-js-loaded";
/**
 * Dispatched on `document` after Rivo accepts a reward redemption, so UI that renders points state it
 * does not own can re-read it (the points activity log — `loyalty-points-activity.js` — listens for this;
 * it is a plain script and cannot import from here, hence the shared string literal).
 */
const REWARD_REDEEMED_EV = "loyalty:reward-redeemed";
/** Gap between reward cards, passed to Swiper as `spaceBetween` (design: 12px). */
const CARD_GAP_PX = 12;
/**
 * Trailing space after the last slide. The rail bleeds past the section's right edge so mid-scroll cards
 * peek off-screen with no margin, but at the end of the scroll the last card should line up with the
 * mini cart's own 12px side padding (`px-3`/`mx-3` on the drawer's content) rather than touch the edge.
 */
const RAIL_END_OFFSET_PX = 12;
/** `carousel.js` is loaded `type="module" fetchpriority="low"`, so `window.initCarousel` may not exist yet. */
const CAROUSEL_RETRY_MS = 100;
const CAROUSEL_MAX_RETRIES = 20;
/**
 * Backstop for a `rivo-js-loaded` that never arrives (Rivo slow, blocked, or erroring). Without it a
 * pending root keeps its skeleton up forever. Matches `loyalty-points-activity.js` and
 * `loyalty-ways-to-earn.js`, which use the same 10s window.
 */
const RIVO_LOAD_TIMEOUT_MS = 10000;

class LoyaltyCart {
  constructor() {
    /** @type {WeakMap<HTMLElement, number>} */
    this.latestRefreshSeqByRoot = new WeakMap();
    this.refreshSeq = 0;
    this.sectionMorphHooked = false;
    this.clickHooked = false;
    this.carouselInitRetries = 0;
    /** Earn accordion state, kept here so cart morphs (which re-render it collapsed) do not close it. */
    this.earnPanelExpanded = false;
    /** True after `document` dispatches `rivo-js-loaded` (set in the handler registered from {@link initialize}). */
    this.rivoLoadReceived = false;
    /**
     * True once {@link RIVO_LOAD_TIMEOUT_MS} has elapsed without Rivo becoming callable. Separate from
     * `rivoLoadReceived` so {@link refreshLoyalty} can tell "still loading" (hold the skeleton) from
     * "not coming" (clear it and hide the root).
     */
    this.rivoLoadTimedOut = false;
    /**
     * Shared `cart.js` read, so concurrent root refreshes issue one request.
     * @type {Promise<LoyaltyCartState> | null}
     */
    this.cartStateInFlight = null;
    /**
     * Discount code of the coupon claim each card is holding, kept off the DOM: the code is never
     * surfaced to the customer, only applied to the cart. Session-lived, like the card itself.
     * @type {WeakMap<HTMLElement, string>}
     */
    this.couponCodeByCard = new WeakMap();
  }

  /**
   * @param {HTMLElement} root
   * @param {number} mySeq
   * @returns {boolean}
   */
  loyaltyRefreshIsLatest(root, mySeq) {
    return this.latestRefreshSeqByRoot.get(root) === mySeq;
  }

  /**
   * @param {unknown} val
   * @returns {string}
   */
  toStr(val) {
    return val == null ? "" : String(val);
  }

  /**
   * @param {unknown} gidOrId
   * @returns {string}
   */
  normalizeRewardId(gidOrId) {
    const str = this.toStr(gidOrId);
    if (!str) return "";
    const parts = str.split("/");
    return parts[parts.length - 1] || str;
  }

  /**
   * @param {unknown} gidOrId
   * @returns {string}
   */
  normProdId(gidOrId) {
    const str = this.toStr(gidOrId);
    if (!str) return "";
    const tail = (str.split("/").pop() || str).split("?")[0];
    return tail || "";
  }

  /**
   * Whether Rivo can be called: either `document` dispatched `rivo-js-loaded`, or the SDK is already
   * on `window`. The second half matters because this module is `type="module" fetchpriority="low"`,
   * so Rivo can dispatch the event before the listener in {@link initialize} exists — the flag alone
   * would then stay false forever with a perfectly usable `window.RivoJS` sitting right there.
   * Call sites that invoke Rivo still verify the specific method they need exists.
   * @returns {boolean}
   */
  isRivoReady() {
    return this.rivoLoadReceived || typeof window.RivoJS?.getCustomerDetails === "function";
  }

  /**
   * @returns {Promise<Record<string, unknown> | null>}
   */
  async loadCustDetails() {
    if (!this.isRivoReady() || typeof window.RivoJS?.getCustomerDetails !== "function") {
      return null;
    }
    try {
      const res = await window.RivoJS.getCustomerDetails();
      return res && typeof res === "object" ? /** @type {Record<string, unknown>} */ (res) : null;
    } catch (error) {
      console.error("[Loyalty Cart] Failed to load customer details", error);
      return null;
    }
  }

  /**
   * @returns {Promise<Record<string, unknown> | null>}
   */
  async loadPtsPurchases() {
    if (!this.isRivoReady() || typeof window.RivoJS?.getCustomerPointsPurchases !== "function") {
      return null;
    }
    try {
      const res = await window.RivoJS.getCustomerPointsPurchases({ used: false });
      return res && typeof res === "object" ? /** @type {Record<string, unknown>} */ (res) : null;
    } catch (error) {
      console.error("[Loyalty Cart] Failed to load points purchases", error);
      return null;
    }
  }

  /**
   * Points balance from getCustomerDetails (nested under customer per Rivo docs).
   * @param {Record<string, unknown> | null} res
   * @returns {number}
   */
  customerPointsTally(res) {
    if (!res || res.success === false) {
      return NaN;
    }
    const cust = res.customer;
    if (cust && typeof cust === "object" && "points_tally" in cust) {
      const num = Number(/** @type {Record<string, unknown>} */ (cust).points_tally);
      return Number.isFinite(num) ? num : NaN;
    }
    if ("points_tally" in res) {
      const num = Number(res.points_tally);
      return Number.isFinite(num) ? num : NaN;
    }
    return NaN;
  }

  /**
   * @param {number} pointsTally
   * @returns {string}
   */
  pointsLineMsg(pointsTally) {
    return Number.isFinite(pointsTally) ? `You have ${Math.round(pointsTally)} pts` : "";
  }

  /**
   * Shares the same points-line message across all loyalty points nodes, regardless of root.
   * @param {number} pointsTally
   */
  syncPointsTally(pointsTally) {
    document.querySelectorAll("[data-loyalty-points-line]").forEach(el => {
      if (!(el instanceof HTMLElement)) return;
      el.textContent = this.pointsLineMsg(pointsTally);
    });
    // Bare formatted balance, for UI that renders the number on its own (rewards grid header).
    if (!Number.isFinite(pointsTally)) return;
    const formatted = Math.round(pointsTally).toLocaleString("en-US");
    document.querySelectorAll("[data-loyalty-points-amount]").forEach(el => {
      if (el instanceof HTMLElement) el.textContent = formatted;
    });
  }

  /**
   * @param {Record<string, unknown> | null} res
   * @returns {Record<string, unknown>[]}
   */
  pointsPurchasesList(res) {
    if (!res || res.success === false || !Array.isArray(res.points_purchases)) {
      return [];
    }
    return /** @type {Record<string, unknown>[]} */ (res.points_purchases);
  }

  /**
   * @param {Record<string, unknown>} purchase
   * @returns {string}
   */
  purchaseProdId(purchase) {
    const prodNest = /** @type {Record<string, unknown> | undefined} */ (
      typeof purchase.product === "object" && purchase.product != null ? purchase.product : undefined
    );
    const rwNest = /** @type {Record<string, unknown> | undefined} */ (
      typeof purchase.reward === "object" && purchase.reward != null ? purchase.reward : undefined
    );
    return this.normProdId(
      purchase.product_id ??
        purchase.shopify_product_id ??
        prodNest?.id ??
        purchase.reward_product_id ??
        rwNest?.shopify_product_id ??
        rwNest?.product_id
    );
  }

  /**
   * @param {Record<string, unknown>} purchase
   * @returns {string}
   */
  purchaseRedeemId(purchase) {
    const rw =
      purchase.reward && typeof purchase.reward === "object"
        ? /** @type {Record<string, unknown>} */ (purchase.reward)
        : null;
    const raw = rw?.id ?? purchase.reward_id ?? purchase.points_reward_id;
    return this.normalizeRewardId(raw);
  }

  /**
   * @param {Record<string, unknown>[]} unusedPurchases
   * @param {string} prodIdNorm
   * @param {string} purchRwIdNorm
   * @returns {Record<string, unknown> | null}
   */
  findClaimedPurchase(unusedPurchases, prodIdNorm, purchRwIdNorm) {
    const found = unusedPurchases.find(purchase => {
      return (
        this.purchaseProdId(purchase) === prodIdNorm &&
        this.normalizeRewardId(this.purchaseRedeemId(purchase)) === purchRwIdNorm
      );
    });
    return found && typeof found === "object" ? found : null;
  }

  /**
   * @param {unknown} res
   * @returns {Record<string, unknown> | null}
   */
  extractRewardFromRedeemRes(res) {
    if (!res || typeof res !== "object") return null;
    const obj = /** @type {Record<string, unknown>} */ (res);
    if (obj.success === false) return null;
    const reward = obj.reward;
    if (reward && typeof reward === "object") return /** @type {Record<string, unknown>} */ (reward);
    const pointsPur = obj.points_purchase;
    if (pointsPur && typeof pointsPur === "object") {
      const nestedReward = /** @type {Record<string, unknown>} */ (pointsPur).reward;
      if (nestedReward && typeof nestedReward === "object")
        return /** @type {Record<string, unknown>} */ (nestedReward);
    }
    return null;
  }

  /**
   * @param {Record<string, unknown> | null} reward
   * @returns {string}
   */
  rewardProductIdFromReward(reward) {
    if (!reward || typeof reward !== "object") return "";
    return this.normProdId(reward.product_id ?? reward.shopify_product_id ?? reward.shopifyProductId);
  }

  /**
   * @param {unknown} rawVid
   * @returns {string}
   */
  normVariantIdForCart(rawVid) {
    const str = this.toStr(rawVid).trim();
    if (!str) return "";
    return this.normProdId(str) || str;
  }

  /**
   * Rivo catalog rewards often expose `variant_ids` (array); see .cursor/references/rivo-loyalty-redemption.md.
   * Discount code for redeem flows comes from {@link pickCodeFromRedeemResByProdId}.
   * @param {Record<string, unknown> | null} reward
   * @returns {string}
   */
  pickVariantIdFromReward(reward) {
    if (!reward || typeof reward !== "object") return "";
    const vNest =
      reward.variant && typeof reward.variant === "object"
        ? /** @type {Record<string, unknown>} */ (reward.variant)
        : null;
    let variantId = reward.shopify_variant_id ?? reward.variant_id ?? reward.shopifyVariantId ?? vNest?.id;
    if (variantId == null || this.toStr(variantId).trim() === "") {
      const variantIds = reward.variant_ids;
      if (Array.isArray(variantIds) && variantIds.length > 0) {
        variantId = variantIds[0];
      } else if (typeof variantIds === "string") {
        const str = variantIds.trim();
        if (str.startsWith("[")) {
          try {
            const parsed = JSON.parse(str);
            if (Array.isArray(parsed) && parsed.length > 0) variantId = parsed[0];
          } catch (error) {
            console.error("[Loyalty Cart] Failed parsing reward variant IDs", error);
          }
        }
        if (variantId == null || this.toStr(variantId).trim() === "") {
          const first = str.split(/[,\s]+/).find(Boolean);
          if (first) variantId = first;
        }
      }
    }
    return this.normVariantIdForCart(variantId);
  }

  /**
   * `redeemReward` may return `points_purchases` or a single `points_purchase`. Code often lives on the purchase row, not on `reward`.
   * @param {Record<string, unknown> | null} rewardRes
   * @returns {Record<string, unknown>[]}
   */
  pointsPurchasesFromRedeemRes(rewardRes) {
    if (!rewardRes || typeof rewardRes !== "object") return [];
    if (Array.isArray(rewardRes.points_purchases)) {
      return /** @type {Record<string, unknown>[]} */ (
        rewardRes.points_purchases.filter(purchase => purchase && typeof purchase === "object")
      );
    }
    const single = rewardRes.points_purchase;
    if (single && typeof single === "object") {
      return [/** @type {Record<string, unknown>} */ (single)];
    }
    return [];
  }

  /**
   * @param {Record<string, unknown> | null} rewardRes
   * @param {string} prodIdNorm
   * @returns {string}
   */
  pickCodeFromRedeemResByProdId(rewardRes, prodIdNorm) {
    const wanted = this.normProdId(prodIdNorm);
    if (!wanted) return "";
    const matchPur = this.pointsPurchasesFromRedeemRes(rewardRes).find(purchase => {
      return this.purchaseProdId(purchase) === wanted;
    });
    return matchPur ? this.pickDiscountCodeFromPurchase(matchPur) : "";
  }

  /**
   * Discount code is often on the points_purchase root, not on reward (runtime log: reward had no code* keys).
   * @param {Record<string, unknown> | null} purchase
   * @returns {string}
   */
  pickDiscountCodeFromPurchase(purchase) {
    if (!purchase || typeof purchase !== "object") return "";
    const direct =
      purchase.discount_code ??
      purchase.code ??
      purchase.redemption_code ??
      purchase.coupon_code ??
      purchase.redeem_code ??
      purchase.shopify_discount_code ??
      purchase.discount_coupon_code;
    let out = this.toStr(direct).trim();
    if (out) return out;
    const meta = purchase.metadata;
    if (meta && typeof meta === "object") {
      const metaRec = /** @type {Record<string, unknown>} */ (meta);
      out = this.toStr(metaRec.discount_code ?? metaRec.code ?? metaRec.redemption_code).trim();
    }
    return out;
  }

  /**
   * @returns {string[]}
   */
  collectCartSectionIds() {
    /** @type {string[]} */
    const ids = [];
    document.querySelectorAll("cart-items-component").forEach(el => {
      if (el instanceof HTMLElement && el.dataset.sectionId && !ids.includes(el.dataset.sectionId)) {
        ids.push(el.dataset.sectionId);
      }
    });
    const discountNode = document.querySelector("cart-discount-component");
    if (
      discountNode instanceof HTMLElement &&
      discountNode.dataset.sectionId &&
      !ids.includes(discountNode.dataset.sectionId)
    ) {
      ids.push(discountNode.dataset.sectionId);
    }
    return ids;
  }

  /**
   * @param {HTMLElement} drawerHost
   * @returns {string}
   */
  readDrawerVariantId(drawerHost) {
    const input = drawerHost.querySelector("[data-variant-id-input]");
    if (!(input instanceof HTMLInputElement)) return "";
    return this.toStr(input.value).trim();
  }

  /**
   * @returns {Promise<string[]>}
   */
  async fetchCartDiscountCodes() {
    const rootPath = window.Shopify?.routes?.root || "/";
    const res = await fetch(`${rootPath}cart.js`);
    if (!res.ok) return [];
    const cartJson = await res.json();
    if (!Array.isArray(cartJson.discount_codes)) return [];
    const discountCodes = /** @type {unknown[]} */ (cartJson.discount_codes);
    return discountCodes
      .filter(
        (/** @type {unknown} */ discountCode) =>
          discountCode && typeof discountCode === "object" && "code" in discountCode
      )
      .map((/** @type {unknown} */ discountCode) =>
        this.toStr(/** @type {Record<string, unknown>} */ (discountCode).code)
      );
  }

  /**
   * Adds the variant (`POST cart/add.js`), then applies the loyalty discount (`POST cart/update.js`).
   * Discount-first left orphan BAL codes and cart/add returned 200 without persisting the line
   * (variant missing from cart.js / section HTML). Add-then-discount matches working cart-discount flows.
   * @param {string | number} variantIdRaw
   * @param {string} discountCode
   * @returns {Promise<Record<string, unknown>>}
   */
  async loyaltyAddVariantWithDiscount(variantIdRaw, discountCode) {
    const variantId = Number(this.toStr(variantIdRaw).trim());
    if (!Number.isFinite(variantId) || variantId < 1) {
      throw new Error("Invalid variant");
    }

    const rootPath = window.Shopify?.routes?.root || "/";
    const themeRoutes = window.Theme?.routes;
    const cartAddUrl = themeRoutes?.cart_add_url || `${rootPath}cart/add.js`;
    const cartUpdateUrl = themeRoutes?.cart_update_url || `${rootPath}cart/update.js`;

    /** @type {{ items: Array<{ id: number; quantity: number }>; sections?: string; sections_url?: string }} */
    const payload = { items: [{ id: variantId, quantity: 1 }] };

    try {
      const utils = /** @type {any} */ (window).GwpCartUtils;
      if (utils?.getItemsToBundle && payload.items) {
        const gwpItems = /** @type {Array<{ id: number; quantity: number }>} */ (utils.getItemsToBundle(0));
        gwpItems.forEach((/** @type {{ id: number; quantity: number }} */ giftItem) => {
          if (!payload.items.some(payloadItem => payloadItem.id === giftItem.id)) {
            payload.items.push(giftItem);
          }
        });
      }
    } catch (error) {
      console.error("[Loyalty Cart] Failed to bundle GWP items", error);
    }

    const sectionIds = this.collectCartSectionIds();
    if (sectionIds.length > 0) {
      payload.sections = sectionIds.join(",");
      payload.sections_url = window.location.pathname;
    }

    // Keep cart in loading state until discount is applied, then morph once with the discounted line.
    document.dispatchEvent(new CustomEvent("cart:add-start"));

    const addRes = await fetch(cartAddUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const addBody = await addRes.json().catch(() => ({}));
    if (!addRes.ok) {
      throw new Error(/** @type {any} */ (addBody).description || "Failed to add to cart");
    }

    const cartData = /** @type {Record<string, unknown>} */ (addBody);
    let sections = /** @type {Record<string, string>} */ (cartData.sections || {});
    /** @type {Record<string, unknown> | null} */
    let discountData = null;

    const code = this.toStr(discountCode).trim();
    if (code) {
      const existing = await this.fetchCartDiscountCodes();
      if (!existing.some(existingCode => existingCode.toUpperCase() === code.toUpperCase())) {
        const merged = [...existing, code].join(",");
        /** @type {Record<string, unknown>} */
        const updateBody = { discount: merged };
        if (sectionIds.length > 0) updateBody.sections = sectionIds.join(",");

        const updateCfg = fetchConfig("json", { body: JSON.stringify(updateBody) });
        const updRes = await fetch(cartUpdateUrl, updateCfg);
        if (!updRes.ok) {
          const errData = await updRes.json().catch(() => ({}));
          throw new Error(/** @type {any} */ (errData).description || "Failed to apply discount");
        }
        discountData = await updRes.json();
        const updSections = /** @type {Record<string, string>} */ ((discountData && discountData.sections) || {});
        if (Object.keys(updSections).length > 0) sections = updSections;
      }
    }

    // Single morph after add (+ discount when present) so the gift never flashes at full price.
    for (const sectionId of sectionIds) {
      if (sections[sectionId]) await morphSection(sectionId, sections[sectionId]);
    }

    document.dispatchEvent(
      new CartAddEvent(discountData || cartData, "loyalty-cart", {
        source: "loyalty",
        itemCount: (discountData && discountData.item_count) || cartData.item_count,
        variantId: String(variantId),
        sections,
      })
    );

    if (discountData) {
      document.dispatchEvent(new DiscountUpdateEvent(discountData, "loyalty-cart", { domSynced: true }));
    }

    return cartData;
  }

  /**
   * Keeps the add icon in the DOM; only label text and Tailwind `hidden` on the labels.
   * @param {HTMLElement} redeemBtn
   * @param {boolean} showAddCopy
   */
  syncRedeemBtnLabels(redeemBtn, showAddCopy) {
    const ctaLbl = redeemBtn.querySelector("[data-loyalty-redeem-cta-label]");
    const addLbl = redeemBtn.querySelector("[data-loyalty-redeem-add-label]");
    if (ctaLbl instanceof HTMLElement && addLbl instanceof HTMLElement) {
      ctaLbl.classList.toggle("hidden", showAddCopy);
      addLbl.classList.toggle("hidden", !showAddCopy);
      ctaLbl.style.removeProperty("display");
      addLbl.style.removeProperty("display");
    }
  }

  /**
   * Busy: spinner + hide icon and labels. Idle: hide spinner + show icon (labels from refreshLoyalty / syncRedeemBtnLabels).
   * @param {HTMLElement} btn
   * @param {boolean} busy
   */
  setCardRedeemBusy(btn, busy) {
    const spinner = btn.querySelector("[data-loyalty-redeem-spinner]");
    const icon = btn.querySelector(".loyaltyCart__redeemIcon");
    const lockIcon = btn.querySelector("[data-loyalty-lock-icon]");
    const ctaLbl = btn.querySelector("[data-loyalty-redeem-cta-label]");
    const addLbl = btn.querySelector("[data-loyalty-redeem-add-label]");
    btn.setAttribute("aria-busy", busy ? "true" : "false");
    if (spinner instanceof HTMLElement) spinner.classList.toggle("hidden", !busy);
    // Busy: spinner only (no icons, no CTA copy). Idle: show add icon; labels restored by refreshLoyalty.
    if (icon instanceof HTMLElement) icon.classList.toggle("hidden", busy);
    if (busy) {
      if (lockIcon instanceof HTMLElement) lockIcon.classList.add("hidden");
      if (ctaLbl instanceof HTMLElement) ctaLbl.classList.add("hidden");
      if (addLbl instanceof HTMLElement) addLbl.classList.add("hidden");
    }
  }

  /**
   * @param {HTMLElement} btn
   * @param {boolean} busy
   */
  setDrawerRedeemBusy(btn, busy) {
    const spinner = btn.querySelector("[data-loyalty-drawer-redeem-spinner]");
    const label = btn.querySelector("[data-loyalty-drawer-redeem-label]");
    btn.setAttribute("aria-busy", busy ? "true" : "false");
    if (spinner instanceof HTMLElement) spinner.classList.toggle("hidden", !busy);
    if (label instanceof HTMLElement) label.classList.toggle("hidden", busy);
  }

  /**
   * @param {HTMLElement} root
   * @param {boolean} isLoading
   */
  setRootLoading(root, isLoading) {
    const loadingNode = root.querySelector("[data-loyalty-loading]");
    // Toggle the Swiper container, never `.swiper-wrapper` — hiding the wrapper leaves Swiper
    // measuring a zero-width track it never recovers from.
    const carousel = root.querySelector("[data-loyalty-carousel]");
    // Non-carousel roots (e.g. the rewards grid section) mark their card container instead.
    const content = root.querySelector("[data-loyalty-content]");
    root.setAttribute("aria-busy", isLoading ? "true" : "false");
    if (loadingNode instanceof HTMLElement) {
      loadingNode.hidden = !isLoading;
    }
    if (carousel instanceof HTMLElement) {
      carousel.hidden = isLoading;
    }
    if (content instanceof HTMLElement) {
      content.hidden = isLoading;
    }
    if (isLoading) {
      root.hidden = false;
      this.setNavArrowsVisible(root, false);
    }
  }

  /**
   * Applies a discount code to the cart without adding a line — the coupon path, which has no
   * variant. Mirrors the discount half of {@link loyaltyAddVariantWithDiscount}: merge with the
   * codes already on the cart, morph the cart sections once, then announce the change.
   * @param {string} discountCode
   * @returns {Promise<{ applied: boolean, applicable: boolean }>}
   */
  async loyaltyApplyDiscountOnly(discountCode) {
    const code = this.toStr(discountCode).trim();
    if (!code) return { applied: false, applicable: false };

    const rootPath = window.Shopify?.routes?.root || "/";
    const cartUpdateUrl = window.Theme?.routes?.cart_update_url || `${rootPath}cart/update.js`;
    const sectionIds = this.collectCartSectionIds();

    const existing = await this.fetchCartDiscountCodes();
    if (existing.some(existingCode => existingCode.toUpperCase() === code.toUpperCase())) {
      return { applied: true, applicable: true };
    }

    /** @type {Record<string, unknown>} */
    const updateBody = { discount: [...existing, code].join(",") };
    if (sectionIds.length > 0) updateBody.sections = sectionIds.join(",");

    const res = await fetch(cartUpdateUrl, fetchConfig("json", { body: JSON.stringify(updateBody) }));
    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(/** @type {any} */ (errData).description || "Failed to apply discount");
    }

    const data = /** @type {Record<string, unknown>} */ (await res.json());
    const sections = /** @type {Record<string, string>} */ (data.sections || {});
    for (const sectionId of sectionIds) {
      if (sections[sectionId]) await morphSection(sectionId, sections[sectionId]);
    }
    document.dispatchEvent(new DiscountUpdateEvent(data, "loyalty-cart", { domSynced: true }));

    // Shopify is the authority on eligibility (loyalty codes carry a minimum subtotal), so
    // report back whether the code actually stuck rather than assuming it did.
    const codes = Array.isArray(data.discount_codes) ? data.discount_codes : [];
    const row = codes.find(
      (/** @type {{ code?: string }} */ entry) => this.toStr(entry?.code).toUpperCase() === code.toUpperCase()
    );
    return { applied: true, applicable: row ? row.applicable !== false : true };
  }

  /**
   * Loyalty discount codes carry a minimum cart total, so a claim made from an empty (or too small)
   * cart would spend the customer's points on a code the cart cannot take. Opens the section's
   * empty-cart pop-up instead and reports that the claim must not run.
   *
   * Roots without the pop-up (the mini-cart rail, which only renders on a non-empty cart) never
   * block, and neither does a cart that could not be read — Shopify still has the final say there.
   * @param {HTMLElement} root
   * @param {Element | null} [openDialog] - Dialog to close first, so two modals never stack.
   * @returns {Promise<boolean>} `true` when the pop-up was shown and the caller must stop.
   */
  async blockedByEmptyCart(root, openDialog = null) {
    const dialog = root.querySelector("[data-loyalty-empty-cart-dialog]");
    if (!(dialog instanceof HTMLElement)) return false;

    const minTotal = Number(root.dataset.loyaltyMinCartTotal || 0);
    const { itemCount, totalPrice } = await this.loyaltyCartState();
    if (itemCount === null || totalPrice === null) return false;
    if (itemCount > 0 && totalPrice >= minTotal) return false;

    const opener = /** @type {any} */ (openDialog);
    // Awaited: closing restores the body scroll lock that showing the pop-up takes again.
    if (opener && typeof opener.closeDialog === "function") await opener.closeDialog();

    const host = /** @type {any} */ (dialog);
    if (typeof host.showDialog === "function") host.showDialog();
    return true;
  }

  /**
   * Opens the cart drawer after a coupon is applied. No-op on the cart template, which has no
   * drawer. Called from an explicit customer action, so it does not consult `auto-open`.
   */
  openCartDrawer() {
    const drawer = document.querySelector("cart-drawer-component");
    if (!(drawer instanceof HTMLElement)) return;
    const host = /** @type {any} */ (drawer);
    if (typeof host.open === "function") host.open();
    else if (typeof host.showDialog === "function") host.showDialog();
  }

  /**
   * Claimed-coupon UI. The discount code is never rendered — a claimed coupon is either already on
   * the cart, in which case the card returns to claimable (badge kept) so another can be claimed,
   * or still to be added, in which case the CTA carries the code and only adds it.
   * @param {HTMLElement} card
   * @param {{ claimed: boolean, addCode: string, missingCode: boolean, ptsNeed: number, pointsTally: number }} state
   */
  setCouponCardState(card, state) {
    const ptsDisp = card.querySelector("[data-loyalty-pts-display]");
    const redeemBtn = card.querySelector("[data-loyalty-redeem]");
    const result = card.querySelector("[data-loyalty-coupon-result]");
    const fallback = card.querySelector("[data-loyalty-coupon-empty]");

    // Rivo confirmed the claim but withheld the code, so there is nothing to add from here: point
    // the customer at their rewards account rather than offer a CTA that cannot work.
    const missingCode = state.claimed && state.missingCode && state.addCode === "";
    const pending = state.claimed && (state.addCode !== "" || missingCode);

    if (fallback instanceof HTMLElement) fallback.hidden = !missingCode;

    if (pending) {
      this.applyCardState(card, "claimed");
      if (ptsDisp instanceof HTMLElement) ptsDisp.textContent = "0 pts";
      if (redeemBtn instanceof HTMLElement) {
        redeemBtn.hidden = missingCode;
        this.syncRedeemBtnLabels(redeemBtn, true);
        redeemBtn.dataset.rewardId = card.dataset.rewardId || "";
        // Flags the CTA as add-only (the code lives in {@link couponCodeByCard}), so a second
        // press adds the claim the customer already paid for instead of claiming again.
        if (state.addCode) {
          this.couponCodeByCard.set(card, state.addCode);
          redeemBtn.dataset.loyaltyCouponAdd = "true";
        } else {
          delete redeemBtn.dataset.loyaltyCouponAdd;
        }
      }
      return;
    }

    // Never claimed, or every claim is already on the cart: the card is claimable again at its
    // points cost. A claim that reached the cart keeps its badge — the customer did claim it.
    const affordable = Number.isFinite(state.pointsTally) && state.pointsTally >= state.ptsNeed;
    this.applyCardState(card, affordable ? "redeem" : "locked", { claimedBadge: state.claimed });
    if (ptsDisp instanceof HTMLElement) ptsDisp.textContent = `${Math.round(state.ptsNeed)} pts`;
    if (redeemBtn instanceof HTMLElement) {
      redeemBtn.hidden = false;
      this.syncRedeemBtnLabels(redeemBtn, false);
      redeemBtn.dataset.rewardId = card.dataset.rewardId || "";
      delete redeemBtn.dataset.loyaltyCouponAdd;
    }
  }

  /**
   * Surfaces why a coupon code did not take effect on the cart (empty cart, minimum subtotal).
   * @param {HTMLElement} card
   * @param {string} message
   */
  setCouponApplyNote(card, message) {
    const note = card.querySelector("[data-loyalty-coupon-note]");
    if (!(note instanceof HTMLElement)) return;
    note.textContent = message;
    note.hidden = message === "";
  }

  /**
   * Puts a claimed coupon's code on the cart and opens the drawer. Applied silently: the customer
   * sees the discount on their cart, never the code.
   * @param {HTMLElement} card
   * @param {string} code
   * @returns {Promise<boolean>} Whether the code reached the cart (`false` leaves the card on Add).
   */
  async applyCouponToCart(card, code) {
    try {
      const { applied, applicable } = await this.loyaltyApplyDiscountOnly(code);
      // Shopify keeps a code it reports as not applicable, so that still counts as on the cart.
      this.setCouponApplyNote(card, applicable ? "" : card.dataset.couponNotApplicableCopy || "");
      if (applied) this.openCartDrawer();
      return applied;
    } catch (error) {
      console.error("[Loyalty Cart] Failed to apply coupon to cart", error);
      this.setCouponApplyNote(card, card.dataset.couponApplyFailedCopy || "");
      return false;
    }
  }

  /**
   * Codes of this reward's unspent claims, in the order Rivo returned them. An entry is `""` when
   * Rivo confirmed the claim but returned no code.
   * @param {Record<string, unknown>[]} unusedPurchases
   * @param {string} rewardIdNorm
   * @returns {string[]}
   */
  couponClaimCodes(unusedPurchases, rewardIdNorm) {
    return unusedPurchases
      .filter(purchase => this.purchaseRedeemId(purchase) === rewardIdNorm)
      .map(purchase => this.pickDiscountCodeFromPurchase(purchase));
  }

  /**
   * Coupon rewards have no Shopify product: they resolve to a discount code instead of a cart line,
   * so they take their own state path (no in-cart product handling, no option drawer). A gift
   * already in the cart does not lock them — the code is issued to the account, not the cart.
   * @param {HTMLElement} card
   * @param {{ unusedPurchases: Record<string, unknown>[], rewardIdNorm: string, ptsNeed: number, pointsTally: number, cartCodes: Set<string> | null }} ctx
   */
  applyCouponCardState(card, ctx) {
    const codes = this.couponClaimCodes(ctx.unusedPurchases, ctx.rewardIdNorm);
    // A code captured from this session's claim survives a stale points-purchases list.
    const localCode = this.couponCodeByCard.get(card) || "";
    if (localCode && !codes.includes(localCode)) codes.push(localCode);

    // An unreadable cart (`null`) counts as "not on the cart": offering Add is harmless, since
    // applying a code the cart already holds is a no-op.
    const onCart = (/** @type {string} */ code) =>
      ctx.cartCodes instanceof Set && ctx.cartCodes.has(code.toUpperCase());
    const addCode = codes.find(code => code !== "" && !onCart(code)) || "";

    this.setCouponCardState(card, {
      claimed: codes.length > 0,
      addCode,
      missingCode: codes.includes(""),
      ptsNeed: ctx.ptsNeed,
      pointsTally: ctx.pointsTally,
    });
  }

  /**
   * Claims a coupon reward: `redeemReward` only — no variant, no cart call. The code comes off the
   * returned points_purchase row, the same place the product flow reads it from, and is parked on
   * the card so the claim survives a points-purchases list that has not caught up yet.
   * @param {HTMLElement} card
   * @param {string} rewardId
   * @returns {Promise<string>} The claimed code, or `""` when Rivo returned none.
   */
  async claimCouponCode(card, rewardId) {
    const res = await this.callRedeemReward(rewardId);
    const rewardIdNorm = this.normalizeRewardId(rewardId);
    const purchases = this.pointsPurchasesFromRedeemRes(res);
    const match = purchases.find(entry => this.purchaseRedeemId(entry) === rewardIdNorm) || purchases[0] || null;
    const code = match ? this.pickDiscountCodeFromPurchase(match) : "";
    if (code) this.couponCodeByCard.set(card, code);
    else console.warn("[Loyalty Cart] coupon redeem returned no discount code", res);
    return code;
  }

  /**
   * The coupon CTA: claim then add, or — for a claim that never reached the cart — add only.
   * `data-loyalty-coupon-add` marks that second case, so a retry never spends points twice.
   * @param {HTMLElement} root
   * @param {HTMLElement} card
   * @param {HTMLElement} btn
   * @param {string} rewardId
   */
  async runCouponCardCta(root, card, btn, rewardId) {
    this.setCardRedeemBusy(btn, true);
    try {
      const addOnly = btn.dataset.loyaltyCouponAdd === "true";
      const code = addOnly ? this.couponCodeByCard.get(card) || "" : await this.claimCouponCode(card, rewardId);
      if (code) await this.applyCouponToCart(card, code);
    } catch (error) {
      console.error("[Loyalty Cart] Failed coupon claim flow", error);
    }
    // Full refresh, as the product path does: it re-reads the balance, the claims and the cart's
    // codes, which together decide whether this card is claimable again.
    try {
      await this.refreshLoyalty(root);
    } catch (error) {
      console.error("[Loyalty Cart] Failed refreshing loyalty UI", error);
    }
    this.setCardRedeemBusy(btn, false);
  }

  /**
   * Locked cards drop `btn-secondary-asphalt` so the grey `--locked` rules (declared in the snippet
   * stylesheet) apply without fighting the `.btn-sm.btn-secondary-asphalt` padding rule.
   * `aria-disabled` rather than `disabled` keeps the `btn` disabled: utilities from overriding the design colours.
   * @param {HTMLElement} card
   * @param {"claimed" | "redeem" | "locked"} state
   * @param {{ claimedBadge?: boolean }} [opts] - Forces the Claimed badge on or off; it otherwise
   *   follows the state. A reward already added to the cart keeps its badge — the customer did claim
   *   it — even though the card is priced at its points cost again.
   */
  applyCardState(card, state, opts = {}) {
    const isLocked = state === "locked";
    const redeemBtn = card.querySelector("[data-loyalty-redeem]");
    const addIcon = card.querySelector(".loyaltyCart__redeemIcon");
    const lockIcon = card.querySelector("[data-loyalty-lock-icon]");
    const badge = card.querySelector("[data-loyalty-claimed-badge]");

    card.dataset.loyaltyCardState = state;
    card.hidden = false;
    card.classList.toggle("loyaltyCart__card--locked", isLocked);

    if (badge instanceof HTMLElement) {
      badge.hidden = !(opts.claimedBadge ?? state === "claimed");
    }
    if (addIcon instanceof HTMLElement) {
      addIcon.classList.toggle("hidden", isLocked);
    }
    if (lockIcon instanceof HTMLElement) {
      lockIcon.classList.toggle("hidden", !isLocked);
    }
    if (redeemBtn instanceof HTMLElement) {
      redeemBtn.classList.toggle("btn-secondary-asphalt", !isLocked);
      redeemBtn.classList.toggle("loyaltyCart__redeemBtn--locked", isLocked);
      if (isLocked) {
        redeemBtn.setAttribute("aria-disabled", "true");
        redeemBtn.setAttribute("tabindex", "-1");
      } else {
        redeemBtn.removeAttribute("aria-disabled");
        redeemBtn.removeAttribute("tabindex");
      }
    }
  }

  /**
   * @param {HTMLElement} root
   * @param {boolean} isVisible
   */
  setNavArrowsVisible(root, isVisible) {
    root.querySelectorAll("[data-loyalty-nav]").forEach(el => {
      if (el instanceof HTMLElement) el.classList.toggle("loyaltyCart__navBtn--visible", isVisible);
    });
  }

  /**
   * Product ids already in the cart with a loyalty discount (`[data-loyalty-in-cart-ids]` from Liquid).
   * Read from a child node so cart morph can refresh the list (root uses `data-skip-node-update`).
   * @param {HTMLElement} root
   * @returns {Set<string>}
   */
  parseInCartIds(root) {
    const ids = new Set();
    const source = root.querySelector("[data-loyalty-in-cart-ids]");
    const raw =
      source instanceof HTMLElement
        ? source.getAttribute("data-loyalty-in-cart-ids") || ""
        : root.dataset.loyaltyInCartIds || "";
    for (const part of raw.split("|")) {
      const prodId = this.normProdId(part);
      if (prodId) ids.add(prodId);
    }
    return ids;
  }

  /**
   * Whether a `cart.js` line carries a loyalty discount. The line exposes its codes as
   * `discounts[].title` and `line_level_discount_allocations[].discount_application.title`; for a
   * discount code both hold the code itself, so this tests the same value as the Liquid loop's
   * `discount.code contains settings.loyalty_discount_prefix`.
   * @param {Record<string, unknown>} item
   * @param {string} prefixUpper
   * @returns {boolean}
   */
  lineHasLoyaltyDiscount(item, prefixUpper) {
    /** @type {string[]} */
    const titles = [];
    if (Array.isArray(item.discounts)) {
      for (const discount of item.discounts) {
        if (discount && typeof discount === "object") titles.push(this.toStr(discount.title));
      }
    }
    if (Array.isArray(item.line_level_discount_allocations)) {
      for (const allocation of item.line_level_discount_allocations) {
        const application = allocation && typeof allocation === "object" ? allocation.discount_application : null;
        if (application && typeof application === "object") {
          titles.push(this.toStr(/** @type {Record<string, unknown>} */ (application).title));
        }
      }
    }
    return titles.some(title => isLoyaltyCode(title, prefixUpper));
  }

  /**
   * The live cart as loyalty reads it: product ids carrying a loyalty discount — the client-side
   * equivalent of the `loyalty_in_cart_ids` Liquid loop, and fresher than it — plus every discount
   * code on the cart, which is how a claimed coupon is recognised as already added.
   *
   * The rewards grid renders `[data-loyalty-in-cart-ids]` server-side and its section is not among
   * the ones morphed after an add ({@link collectCartSectionIds} only collects the cart sections),
   * so once a claimed reward lands in the cart that node is stale. Rivo also keeps the points
   * purchase `used_at: null` until the order is placed, so without this the card keeps resolving to
   * `claimed` at `0 pts` even though the purchase is spent, and a second Add would cost the customer.
   *
   * @returns {Promise<LoyaltyCartState>} `null` on any field means "cannot tell" (cart unreadable, or
   *   no loyalty discount prefix configured): callers keep the server-rendered id list, treat a claimed
   *   coupon as not yet added, and let a claim through rather than blocking it.
   */
  async readLoyaltyCartState() {
    const cfg = getLoyaltyDiscountCfg();
    const rootPath = window.Shopify?.routes?.root || "/";
    try {
      const res = await fetch(`${rootPath}cart.js`);
      if (!res.ok) return UNREAD_CART_STATE;
      const cartJson = await res.json();
      if (!cartJson || typeof cartJson !== "object") return UNREAD_CART_STATE;

      /** @type {Set<string> | null} */
      let prodIds = null;
      if (cfg.prefixUpper && Array.isArray(cartJson.items)) {
        prodIds = new Set();
        for (const item of cartJson.items) {
          if (!item || typeof item !== "object") continue;
          if (!this.lineHasLoyaltyDiscount(item, cfg.prefixUpper)) continue;
          const prodId = this.normProdId(item.product_id);
          if (prodId) prodIds.add(prodId);
        }
      }

      // Upper-cased, matching how codes are compared everywhere else.
      const codes = new Set();
      const discountCodes = Array.isArray(cartJson.discount_codes) ? cartJson.discount_codes : [];
      for (const entry of discountCodes) {
        if (!entry || typeof entry !== "object") continue;
        const code = this.toStr(/** @type {Record<string, unknown>} */ (entry).code)
          .trim()
          .toUpperCase();
        if (code) codes.add(code);
      }

      return {
        prodIds,
        codes,
        itemCount: typeof cartJson.item_count === "number" ? cartJson.item_count : null,
        totalPrice: typeof cartJson.total_price === "number" ? cartJson.total_price : null,
      };
    } catch (error) {
      console.error("[Loyalty Cart] Failed to read the loyalty state of the cart", error);
      return UNREAD_CART_STATE;
    }
  }

  /**
   * {@link readLoyaltyCartState}, shared between root refreshes that overlap (the mini-cart rail
   * and the rewards grid both refresh on the same pass) so they issue one `cart.js` request.
   * @returns {Promise<LoyaltyCartState>}
   */
  async loyaltyCartState() {
    if (this.cartStateInFlight) return this.cartStateInFlight;
    this.cartStateInFlight = this.readLoyaltyCartState();
    try {
      return await this.cartStateInFlight;
    } finally {
      this.cartStateInFlight = null;
    }
  }

  /**
   * Reorders visible reward cards in the track. When `claimedFirst` is true, Claimed cards lead
   * (among themselves by points); otherwise sort by ascending points only.
   * @param {HTMLElement} track
   * @param {{ claimedFirst: boolean }} opts
   */
  sortLoyaltyCards(track, opts) {
    const cards = [...track.querySelectorAll("[data-loyalty-card]")].filter(
      card => card instanceof HTMLElement && !card.hidden
    );
    cards.sort((a, b) => {
      if (!(a instanceof HTMLElement) || !(b instanceof HTMLElement)) return 0;
      if (opts.claimedFirst) {
        const aClaimed = a.dataset.loyaltyCardState === "claimed" ? 0 : 1;
        const bClaimed = b.dataset.loyaltyCardState === "claimed" ? 0 : 1;
        if (aClaimed !== bClaimed) return aClaimed - bClaimed;
      }
      return Number(a.dataset.pointsCost || 0) - Number(b.dataset.pointsCost || 0);
    });
    for (const card of cards) {
      track.appendChild(card);
    }
  }

  /**
   * Sync Swiper nav disabled state after slide hide/reorder (handlers attach on init only).
   * @param {HTMLElement} root
   */
  syncNavArrowState(root) {
    const host = root.querySelector("[data-loyalty-carousel]");
    if (!(host instanceof HTMLElement)) return;
    const carouselInst = /** @type {any} */ (host)._carouselInstance;
    const swiper = carouselInst && typeof carouselInst.getInstance === "function" ? carouselInst.getInstance() : null;
    if (!swiper) return;
    const prevEl = root.querySelector('[data-loyalty-nav="prev"]');
    const nextEl = root.querySelector('[data-loyalty-nav="next"]');
    const updateNav = /** @type {any} */ (window).updateNavigationVisibility;
    if (typeof updateNav === "function" && prevEl && nextEl) {
      updateNav(swiper, prevEl, nextEl);
    }
  }

  /**
   * `useDefault: false` keeps carousel.js's shared defaults (pagination, autoplay, breakpoints) out —
   * the rail only wants free-scrolling auto-width slides plus the two arrows.
   * `observer`/`observeParents`/`resizeObserver` matter here specifically: the rail is first laid out
   * inside a closed drawer where every box is 0px, so Swiper has to re-measure when the drawer opens.
   * @param {Element | null} prevEl
   * @param {Element | null} nextEl
   * @returns {Record<string, unknown>}
   */
  carouselConfig(prevEl, nextEl) {
    return {
      useDefault: false,
      slidesPerView: "auto",
      spaceBetween: CARD_GAP_PX,
      slidesOffsetAfter: RAIL_END_OFFSET_PX,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      resizeObserver: true,
      // Our markup already carries role="list"/listitem and arrow aria-labels.
      a11y: { enabled: false },
      navigation: { prevEl, nextEl },
    };
  }

  /**
   * Idempotent: re-running updates an existing instance instead of stacking a new one.
   * @param {HTMLElement} root
   */
  ensureCarousel(root) {
    const host = root.querySelector("[data-loyalty-carousel]");
    if (!(host instanceof HTMLElement)) return;

    const existing = /** @type {any} */ (host)._carouselInstance;
    if (existing && typeof existing.isInitialized === "function" && existing.isInitialized()) {
      existing.getInstance()?.update();
      this.syncNavArrowState(root);
      return;
    }

    const initCarousel = /** @type {any} */ (window).initCarousel;
    if (typeof initCarousel !== "function") {
      if (this.carouselInitRetries < CAROUSEL_MAX_RETRIES) {
        this.carouselInitRetries++;
        setTimeout(() => this.ensureCarousel(root), CAROUSEL_RETRY_MS);
      } else {
        console.error("[Loyalty Cart] carousel.js never exposed initCarousel; reward rail will not scroll.");
      }
      return;
    }

    const prevEl = root.querySelector('[data-loyalty-nav="prev"]');
    const nextEl = root.querySelector('[data-loyalty-nav="next"]');
    initCarousel(host, this.carouselConfig(prevEl, nextEl));
    this.syncNavArrowState(root);
  }

  /**
   * Re-applies the accordion state to every mini-cart earn callout, so a cart morph (which always
   * renders it collapsed) does not slam a panel the customer just opened.
   */
  applyEarnPanelState() {
    document.querySelectorAll("[data-loyalty-earn-toggle]").forEach(toggle => {
      if (!(toggle instanceof HTMLElement)) return;
      const root = toggle.closest("[data-loyalty-earn-root]");
      const panel = root instanceof HTMLElement ? root.querySelector("[data-loyalty-earn-panel]") : null;
      toggle.setAttribute("aria-expanded", this.earnPanelExpanded ? "true" : "false");
      if (panel instanceof HTMLElement) {
        panel.hidden = !this.earnPanelExpanded;
      }
    });
  }

  /**
   * @param {HTMLElement} root
   * @returns {Promise<void>}
   */
  async refreshLoyalty(root) {
    const mySeq = ++this.refreshSeq;
    this.latestRefreshSeqByRoot.set(root, mySeq);
    this.setRootLoading(root, true);

    const carousel = root.querySelector("[data-loyalty-carousel]");
    const track = root.querySelector("[data-loyalty-track]");
    const cards = root.querySelectorAll("[data-loyalty-card]");
    const serverInCartIds = this.parseInCartIds(root);
    // Opt-in (rewards grid section): cart contents do not gate the catalogue. A reward the
    // customer already added stays in the list and resolves back to claimable, so it can be
    // claimed again. Absent on the mini-cart rail, which still hides it and locks the rest.
    const ignoreCart = root.dataset.loyaltyIgnoreCart === "true";

    // Rivo has not answered yet. Every other bail-out below clears the loading state; this one used
    // to return with the skeleton still up and nothing scheduled to take it down, which is how a
    // missed `rivo-js-loaded` turned into a skeleton that never went away. The skeleton is still the
    // right thing to show inside the normal (sub-second) wait — hiding the root there would flash
    // the section's heading out and back — so it is held only until the
    // {@link RIVO_LOAD_TIMEOUT_MS} backstop in {@link initialize} declares the load dead, at which
    // point this resolves to the same hidden state as a missing `window.RivoJS`.
    if (!this.isRivoReady()) {
      if (this.rivoLoadTimedOut && this.loyaltyRefreshIsLatest(root, mySeq)) {
        this.setRootLoading(root, false);
        root.hidden = true;
      }
      return;
    }

    if (typeof window.RivoJS === "undefined" || window.RivoJS == null) {
      if (this.loyaltyRefreshIsLatest(root, mySeq)) {
        this.setRootLoading(root, false);
        root.hidden = true;
      }
      return;
    }

    if (root.dataset.customerAuth !== "true") {
      if (this.loyaltyRefreshIsLatest(root, mySeq)) {
        this.setRootLoading(root, false);
        root.hidden = true;
      }
      return;
    }

    const [details, purchasesRes, cartState] = await Promise.all([
      this.loadCustDetails(),
      this.loadPtsPurchases(),
      this.loyaltyCartState(),
    ]);
    if (!this.loyaltyRefreshIsLatest(root, mySeq)) return;

    // The live cart replaces the server-rendered list rather than merging with it: it is the same
    // data the Liquid loop was built from, only current, so it also reports a gift the customer has
    // since removed (which returns that card to claimed, its purchase still unspent).
    const inCartIds = cartState.prodIds ?? serverInCartIds;
    const giftInCart = !ignoreCart && inCartIds.size > 0;

    const pointsTally = this.customerPointsTally(details);
    this.syncPointsTally(pointsTally);
    const purchases = this.pointsPurchasesList(purchasesRes);
    const unusedPurchases = purchases.filter(purchase => purchase && purchase.used_at == null);
    // Signed-in customers see the rail at any balance (0–99 point customers get the locked catalogue).
    // A non-finite tally means Rivo did not answer — stay hidden rather than render everything as locked,
    // unless there is a claimed reward worth surfacing regardless of balance, or a gift already in cart.
    const canShow = Number.isFinite(pointsTally) || unusedPurchases.length > 0 || giftInCart;

    if (!canShow) {
      if (this.loyaltyRefreshIsLatest(root, mySeq)) {
        this.setRootLoading(root, false);
        root.hidden = true;
      }
      return;
    }

    const claimedByProd = new Map();
    const claimedByRw = new Map();
    for (const purchase of unusedPurchases) {
      const redeemVal = this.purchaseRedeemId(purchase);
      const prodId = this.purchaseProdId(purchase);
      if (prodId && redeemVal && !claimedByProd.has(prodId)) {
        claimedByProd.set(prodId, redeemVal);
      }
      if (redeemVal && !claimedByRw.has(redeemVal)) {
        claimedByRw.set(redeemVal, redeemVal);
      }
    }

    for (const card of cards) {
      if (!(card instanceof HTMLElement)) continue;
      const prodId = this.normProdId(card.dataset.productId || "");
      const catalogRwId = card.dataset.rewardId || "";
      const catalogRwNorm = this.normalizeRewardId(catalogRwId);
      const ptsNeed = Number(card.dataset.pointsCost || 0);
      const redeemBtn = card.querySelector("[data-loyalty-redeem]");
      const ptsDisp = card.querySelector("[data-loyalty-pts-display]");

      // Coupon rewards (rewards grid only) resolve to a discount code, not a cart line.
      if (card.dataset.loyaltyCoupon === "true") {
        this.applyCouponCardState(card, {
          unusedPurchases,
          rewardIdNorm: catalogRwNorm,
          ptsNeed,
          pointsTally,
          cartCodes: cartState.codes,
        });
        continue;
      }

      // Gift already redeemed into cart: the mini-cart drops that card from the rail.
      const inCart = prodId !== "" && inCartIds.has(prodId);
      if (!ignoreCart && inCart) {
        card.hidden = true;
        continue;
      }

      if (giftInCart) {
        this.applyCardState(card, "locked");
        if (ptsDisp instanceof HTMLElement) {
          ptsDisp.textContent = `${Math.round(ptsNeed)} pts`;
        }
        if (redeemBtn instanceof HTMLElement) {
          this.syncRedeemBtnLabels(redeemBtn, false);
          redeemBtn.dataset.rewardId = catalogRwId;
          delete redeemBtn.dataset.loyaltyVariantId;
          delete redeemBtn.dataset.loyaltyRewardCode;
        }
        continue;
      }

      // A reward sitting in the cart is deliberately treated as unclaimed: its points purchase
      // is spent, so the card returns to "claim" — showing the reward's points cost rather than
      // `0 pts` — and a further click runs a fresh redeemReward rather than re-adding the same
      // purchase, which is what that click would now cost the customer. The Claimed badge stays on
      // (passed explicitly below), since the reward the customer claimed is in their cart.
      const purchRwId = inCart ? "" : claimedByProd.get(prodId) || claimedByRw.get(catalogRwNorm) || "";
      const isClaimed = Boolean(purchRwId);

      if (isClaimed) {
        this.applyCardState(card, "claimed");
        if (ptsDisp instanceof HTMLElement) {
          ptsDisp.textContent = "0 pts";
        }
        if (redeemBtn instanceof HTMLElement) {
          this.syncRedeemBtnLabels(redeemBtn, true);
          redeemBtn.dataset.rewardId = purchRwId || catalogRwId;
          const purchRwNorm = this.normalizeRewardId(purchRwId);
          const matchPur = this.findClaimedPurchase(unusedPurchases, prodId, purchRwNorm);
          const rwObj =
            matchPur?.reward && typeof matchPur.reward === "object"
              ? /** @type {Record<string, unknown>} */ (matchPur.reward)
              : null;
          const finalVid = this.pickVariantIdFromReward(rwObj);
          const finalCode = matchPur && typeof matchPur === "object" ? this.pickDiscountCodeFromPurchase(matchPur) : "";
          if (finalVid) redeemBtn.dataset.loyaltyVariantId = finalVid;
          else delete redeemBtn.dataset.loyaltyVariantId;
          if (finalCode) redeemBtn.dataset.loyaltyRewardCode = finalCode;
          else delete redeemBtn.dataset.loyaltyRewardCode;
        }
        continue;
      }

      // Unaffordable rewards stay on the rail as a locked teaser instead of being hidden.
      const affordable = Number.isFinite(pointsTally) && pointsTally >= ptsNeed;
      this.applyCardState(card, affordable ? "redeem" : "locked", { claimedBadge: inCart });
      if (ptsDisp instanceof HTMLElement) {
        ptsDisp.textContent = `${Math.round(ptsNeed)} pts`;
      }
      if (redeemBtn instanceof HTMLElement) {
        this.syncRedeemBtnLabels(redeemBtn, false);
        redeemBtn.dataset.rewardId = catalogRwId;
        delete redeemBtn.dataset.loyaltyVariantId;
        delete redeemBtn.dataset.loyaltyRewardCode;
      }
    }

    if (track instanceof HTMLElement) {
      this.sortLoyaltyCards(track, { claimedFirst: !giftInCart });
    }

    if ([...cards].every(card => card instanceof HTMLElement && card.hidden)) {
      if (this.loyaltyRefreshIsLatest(root, mySeq)) {
        this.setRootLoading(root, false);
        root.hidden = true;
      }
      return;
    }

    if (!this.loyaltyRefreshIsLatest(root, mySeq)) return;

    this.setRootLoading(root, false);
    if (carousel instanceof HTMLElement) {
      carousel.hidden = false;
    }
    const contentNode = root.querySelector("[data-loyalty-content]");
    if (contentNode instanceof HTMLElement) {
      contentNode.hidden = false;
    }
    root.hidden = false;
    // Init after the rail is visible so Swiper measures real boxes on the first pass.
    this.ensureCarousel(root);
    this.setNavArrowsVisible(root, true);
    this.syncNavArrowState(root);
  }

  /**
   * Syncs points tally for compact loyalty UI when reward is already in cart.
   * @returns {Promise<void>}
   */
  async refreshPointsTally() {
    if (!this.isRivoReady() || typeof window.RivoJS === "undefined" || window.RivoJS == null) {
      this.syncPointsTally(NaN);
      return;
    }
    const details = await this.loadCustDetails();
    const pointsTally = this.customerPointsTally(details);
    this.syncPointsTally(pointsTally);
  }

  /**
   * @param {string} handle
   * @returns {Promise<string>}
   */
  async fetchOptionsSection(handle) {
    const rootPath = window.Shopify?.routes?.root || "/";
    const url = `${rootPath}products/${encodeURIComponent(handle)}?sections=loyalty-product-options`;
    const res = await fetch(url);
    if (!res.ok) throw new Error("Section fetch failed");
    const data = await res.json();
    const html = data["loyalty-product-options"];
    return typeof html === "string" ? html : "";
  }

  /**
   * @param {HTMLElement} host
   * @param {string} html
   */
  injectSectionHtml(host, html) {
    host.innerHTML = html;
    host.querySelectorAll("script").forEach(oldScript => {
      const newScript = document.createElement("script");
      Array.from(oldScript.attributes).forEach(attr => {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.textContent = oldScript.textContent;
      oldScript.replaceWith(newScript);
    });
  }

  refreshAllLoyaltyRoots() {
    document.querySelectorAll("[data-loyalty-cart-root]").forEach(el => {
      if (el instanceof HTMLElement) void this.refreshLoyalty(el);
    });
    void this.refreshPointsTally();
  }

  /**
   * Rivo redeemReward expects a numeric reward id when using integer catalog ids.
   * RivoJS functions return a Promise per vendor docs.
   * @param {string | number} rewardIdRaw
   * @returns {Promise<Record<string, unknown> | null>}
   */
  async callRedeemReward(rewardIdRaw) {
    if (!this.isRivoReady() || typeof window.RivoJS?.redeemReward !== "function") return null;
    const str = this.toStr(rewardIdRaw).trim();
    const rewardNum = Number(str);
    const out =
      /^\d+$/.test(str) && Number.isFinite(rewardNum)
        ? window.RivoJS.redeemReward(rewardNum)
        : window.RivoJS.redeemReward(rewardIdRaw);
    const settled = await Promise.resolve(out);
    if (!settled || typeof settled !== "object") return null;
    if (settled.success !== false) {
      document.dispatchEvent(
        new CustomEvent(REWARD_REDEEMED_EV, { detail: { rewardId: rewardIdRaw, response: settled } })
      );
    }
    return /** @type {Record<string, unknown>} */ (settled);
  }

  setupDelegation() {
    if (this.clickHooked) return;
    this.clickHooked = true;

    document.addEventListener("click", async event => {
      const target = event.target;
      // Chevron SVG is not an HTMLElement; Element still supports closest().
      if (!(target instanceof Element)) return;

      const earnToggle = target.closest("[data-loyalty-earn-toggle]");
      if (earnToggle instanceof HTMLElement) {
        this.earnPanelExpanded = earnToggle.getAttribute("aria-expanded") !== "true";
        this.applyEarnPanelState();
        return;
      }

      // Arrow clicks are handled by Swiper's navigation module, not here — but they must not fall
      // through to the redeem handler below when an arrow overlaps a card.
      if (target.closest("[data-loyalty-nav]")) return;

      const redeemBtn = target.closest("[data-loyalty-redeem]");
      if (redeemBtn instanceof HTMLElement) {
        const root = redeemBtn.closest("[data-loyalty-cart-root]");
        if (!(root instanceof HTMLElement)) return;

        // Locked rewards are inert: `pointer-events: none` normally stops this, but a keyboard
        // Enter on a focused button (or a click retargeted by a child) can still land here.
        const lockedCard = redeemBtn.closest('[data-loyalty-card-state="locked"]');
        if (lockedCard || redeemBtn.getAttribute("aria-disabled") === "true") return;

        const rewardId = redeemBtn.dataset.rewardId || "";
        const handle = redeemBtn.dataset.productHandle || "";
        const isMulti = redeemBtn.dataset.multiOption === "true";
        if (!rewardId) return;

        // Ahead of the claim, the option drawer and the cart: nothing is spent on a cart that
        // cannot take the discount.
        if (await this.blockedByEmptyCart(root)) return;

        // Coupon rewards never open the option drawer: they claim a code and apply it to the cart.
        const couponCard = redeemBtn.closest('[data-loyalty-coupon="true"]');
        if (couponCard instanceof HTMLElement) {
          await this.runCouponCardCta(root, couponCard, redeemBtn, rewardId);
          return;
        }

        const drawerHost = root.querySelector("[data-loyalty-option-drawer-body]");
        const dialog = root.querySelector("[data-loyalty-dialog]");

        if (isMulti && handle && drawerHost instanceof HTMLElement && dialog instanceof HTMLElement) {
          drawerHost.innerHTML = `<p class="text-14-regular text-asphalt px-4 py-8">Loading…</p>`;
          if (typeof dialog.showDialog === "function") {
            dialog.showDialog();
          }
          try {
            const html = await this.fetchOptionsSection(handle);
            this.injectSectionHtml(drawerHost, html);
            const confirmBtn = root.querySelector("[data-loyalty-drawer-redeem]");
            const cardEl = redeemBtn.closest("[data-loyalty-card]");
            if (confirmBtn instanceof HTMLElement) {
              confirmBtn.dataset.rewardId = rewardId;
              if (cardEl instanceof HTMLElement) {
                confirmBtn.dataset.loyaltyCardState = cardEl.dataset.loyaltyCardState || "redeem";
                confirmBtn.dataset.loyaltyProductId = cardEl.dataset.productId || "";
                confirmBtn.dataset.loyaltyRewardCode = redeemBtn.dataset.loyaltyRewardCode || "";
              }
            }
          } catch (error) {
            console.error("[Loyalty Cart] Failed to load loyalty options section", error);
            drawerHost.innerHTML = `<p class="text-14-regular text-asphalt px-4 py-6">Options unavailable. Try again.</p>`;
          }
          return;
        }

        const cardEl = redeemBtn.closest("[data-loyalty-card]");
        const cardState = cardEl instanceof HTMLElement ? cardEl.dataset.loyaltyCardState || "redeem" : "redeem";
        const prodIdNorm = this.normProdId(cardEl instanceof HTMLElement ? cardEl.dataset.productId || "" : "");

        if (cardState === "claimed" || this.isRivoReady()) {
          this.setCardRedeemBusy(redeemBtn, true);
          try {
            if (cardState === "claimed") {
              const variantId = redeemBtn.dataset.loyaltyVariantId || "";
              const discountCode = redeemBtn.dataset.loyaltyRewardCode || "";
              if (variantId) await this.loyaltyAddVariantWithDiscount(variantId, discountCode);
            } else {
              const redeemRes = await this.callRedeemReward(rewardId);
              const reward = this.extractRewardFromRedeemRes(redeemRes);
              if (reward && redeemRes) {
                const rwProdId = this.rewardProductIdFromReward(reward);
                const variantId = this.pickVariantIdFromReward(reward);
                const code = this.pickCodeFromRedeemResByProdId(redeemRes, prodIdNorm);
                if (rwProdId && rwProdId === prodIdNorm) {
                  if (variantId) await this.loyaltyAddVariantWithDiscount(variantId, code);
                }
              }
            }
          } catch (error) {
            console.error("[Loyalty Cart] Failed loyalty card redeem flow", error);
          }
          try {
            await this.refreshLoyalty(root);
          } catch (error) {
            console.error("[Loyalty Cart] Failed refreshing loyalty UI", error);
          }
          this.setCardRedeemBusy(redeemBtn, false);
        }
        return;
      }

      const drawerRedeem = target.closest("[data-loyalty-drawer-redeem]");
      if (drawerRedeem instanceof HTMLElement) {
        const root = drawerRedeem.closest("[data-loyalty-cart-root]");
        if (!(root instanceof HTMLElement)) return;

        const dialog = root.querySelector("[data-loyalty-dialog]");
        const rewardId = drawerRedeem.dataset.rewardId || "";
        const cardState = drawerRedeem.dataset.loyaltyCardState || "redeem";
        const prodId = this.normProdId(drawerRedeem.dataset.loyaltyProductId || "");
        const drawerHost = root.querySelector("[data-loyalty-option-drawer-body]");
        const variantId = drawerHost instanceof HTMLElement ? this.readDrawerVariantId(drawerHost) : "";
        const rewardCode = drawerRedeem.dataset.loyaltyRewardCode || "";

        if (!rewardId || !variantId) return;
        if (cardState !== "claimed" && !this.isRivoReady()) return;

        // The cart can empty while the option drawer sits open, so the guard runs again here.
        if (await this.blockedByEmptyCart(root, dialog)) return;

        this.setDrawerRedeemBusy(drawerRedeem, true);
        try {
          if (cardState === "claimed") {
            await this.loyaltyAddVariantWithDiscount(variantId, rewardCode);
          } else {
            const redeemRes = await this.callRedeemReward(rewardId);
            const reward = this.extractRewardFromRedeemRes(redeemRes);
            if (reward && redeemRes) {
              const rwProdId = this.rewardProductIdFromReward(reward);
              const code = this.pickCodeFromRedeemResByProdId(redeemRes, prodId);
              if (rwProdId && rwProdId === prodId) {
                await this.loyaltyAddVariantWithDiscount(variantId, code);
              }
            }
          }
          await this.refreshLoyalty(root);
          if (dialog instanceof HTMLElement && typeof dialog.closeDialog === "function") {
            dialog.closeDialog();
          }
        } catch (error) {
          console.error("[Loyalty Cart] Failed loyalty drawer redeem flow", error);
        } finally {
          this.setDrawerRedeemBusy(drawerRedeem, false);
        }
      }
    });
  }

  /**
   * Re-run loyalty UI logic after the cart section (or any morphed section) updates the DOM.
   * Idempotent delegation; refreshes every `[data-loyalty-cart-root]` (e.g. block injected again after removing a loyalty discount line).
   */
  reinitializeLoyaltyCart() {
    this.setupDelegation();
    this.applyEarnPanelState();
    this.refreshAllLoyaltyRoots();
  }

  initialize() {
    this.setupDelegation();
    this.applyEarnPanelState();

    document.addEventListener(RIVO_LOAD_EV, () => {
      this.rivoLoadReceived = true;
      this.refreshAllLoyaltyRoots();
    });

    // Fires whether or not Rivo ever answers. Two jobs: pick up an SDK whose event this listener was
    // registered too late to hear (`isRivoReady` sees `window.RivoJS` directly), and, when Rivo is
    // genuinely absent, let the pending guard in `refreshLoyalty` resolve instead of holding a
    // skeleton forever.
    window.setTimeout(() => {
      this.rivoLoadTimedOut = true;
      this.refreshAllLoyaltyRoots();
    }, RIVO_LOAD_TIMEOUT_MS);

    if (!this.sectionMorphHooked) {
      this.sectionMorphHooked = true;
      document.addEventListener(ThemeEvents.sectionMorph, () => {
        queueMicrotask(() => this.reinitializeLoyaltyCart());
      });
    }
  }
}

const loyaltyCart = new LoyaltyCart();

// Run at module evaluation (not DOMContentLoaded): section morph may inject the rail after load,
// and listeners on `document` survive; catch roots that appear later via reinitializeLoyaltyCart.
loyaltyCart.initialize();
// Catch roots when this module loads after `section:morph` (e.g. loyalty block first injected on cart morph).
queueMicrotask(() => loyaltyCart.reinitializeLoyaltyCart());
